import React, { useEffect, useState } from 'react';

import { Alert, Button, Skeleton } from 'antd';

import { useNavigate } from 'react-router-dom';

import FieldsMap from './components/FieldsMap';

import { monitorAPI } from '../../http/MonitorAPI';
import { FieldSummary } from '../../models/FieldsSummary';

const IndexPage = () => {
    const navigate = useNavigate();
    const [fields, setFields] = useState<FieldSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const load = () => {
        setLoading(true);
        setError('');
        monitorAPI
            .GetFieldsSummary()
            .then((data) => setFields(data || []))
            .catch(() => setError('Не удалось загрузить поля'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    if (loading) {
        return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    if (error) {
        return (
            <Alert
                type="error"
                message={error}
                action={
                    <Button size="small" onClick={load}>
                        Повторить
                    </Button>
                }
            />
        );
    }

    if (fields.length === 0) {
        return (
            <div className="empty-box">
                <h3>Нет полей</h3>
                <p>Добавьте поле, чтобы видеть влагу, осадки и фазы культур.</p>
                <Button type="primary" onClick={() => navigate('/settings/fields')}>
                    Добавить поле
                </Button>
            </div>
        );
    }

    // ===== KPI: светофор состояния полей =====
    const belowNorm = fields.filter((f) => f.moisture_status === 'below').length;

    const aboveNorm = fields.filter((f) => f.moisture_status === 'above').length;

    const frostCount = fields.filter((f) => {
        const m = f.latest?.metrics || {};
        return m.is_frost === 1;
    }).length;

    // Без данных: нет станции ИЛИ статус none (нет свежих метрик)
    const noData = fields.filter((f) => !f.station || f.status === 'none').length;

    // ===== Требуют действия =====
    const actions: { field: FieldSummary; level: 'danger' | 'warn'; reason: string }[] = [];
    fields.forEach((f) => {
        const m = f.latest?.metrics || {};
        if (m.is_frost === 1) {
            actions.push({ field: f, level: 'danger', reason: 'Заморозок' });
        } else if (f.status === 'none') {
            actions.push({ field: f, level: 'danger', reason: 'Нет данных' });
        } else if (!f.station) {
            actions.push({ field: f, level: 'warn', reason: 'Нет станции — данные только из Open-Meteo' });
        } else if (f.status === 'warn') {
            actions.push({ field: f, level: 'warn', reason: 'Неполные или устаревшие данные' });
        }
    });

    const fmt = (v: number | null, unit: string): string => (v === null ? '—' : `${v.toFixed(0)} ${unit}`);

    return (
        <>
            <div className="kpi-row">
                <div className="kpi">
                    <div className="kpi-label">Влага ниже нормы</div>
                    <div className="kpi-value">{belowNorm} <small>полей</small></div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Влага выше нормы</div>
                    <div className="kpi-value">{aboveNorm} <small>полей</small></div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Заморозки за 24 ч</div>
                    <div className="kpi-value">{frostCount} <small>полей</small></div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Без данных</div>
                    <div className="kpi-value">{noData} <small>полей</small></div>
                </div>
            </div>

            {actions.length === 0 ? (
                <Alert
                    type="success"
                    showIcon
                    message="Все поля в норме — действий не требуется"
                    style={{ marginBottom: 16 }}
                />
            ) : (
                <div className="card" style={{ marginBottom: 16 }}>
                    <h3 className="card-title">Требуют действия</h3>
                    {actions.map((a) => (
                        <div className="action-row" key={a.field.id}>
                            <span className={`action-dot ${a.level}`} />
                            <div className="action-text">
                                <b>
                                    {a.field.name || `Поле №${a.field.id}`}
                                    {a.field.season?.culture_name ? ` · ${a.field.season.culture_name}` : ''}
                                    {a.field.season?.current_phase ? ` · фаза: ${a.field.season.current_phase}` : ''}
                                </b>
                                <span>{a.reason}</span>
                            </div>
                            <Button size="small" onClick={() => navigate(`/fields/${a.field.id}`)}>
                                Открыть
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div className="card">
                <h3 className="card-title">Карта полей</h3>
                <FieldsMap fields={fields} />
            </div>
        </>
    );
};

export default IndexPage;
